import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from '@mantine/core';
import { Link } from 'wouter';
import {
  PiBagSimple,
  PiCaretDown,
  PiChatCircle,
  PiCursorClick,
  PiEnvelopeSimple,
  PiGitBranch,
  PiReceipt,
  PiShieldCheck,
  PiTrash,
  PiUserSwitch,
} from 'react-icons/pi';
import dayjs from 'dayjs';
import {
  TOfferActivity,
  useAddOfferComment,
  useDeleteOfferComment,
  useOfferComments,
} from '@/models/offers';
import { formatActivity } from './OfferActivityTimeline';
import classes from './OfferActivityFeed.module.css';

/** Split-workflow audit event. Cycle 3 will source this from the backend split_plan log. */
export type SplitAuditEvent = {
  id: string;
  time: string;
  actor: string;
  action: string;
  detail?: string;
};

type Category =
  | 'note'
  | 'offer'
  | 'email'
  | 'engagement'
  | 'kyc'
  | 'payment'
  | 'payer'
  | 'split';

const CATEGORIES: Record<
  Category,
  { label: string; color: string; icon: typeof PiBagSimple }
> = {
  note: { label: 'Note', color: 'yellow', icon: PiChatCircle },
  offer: { label: 'Offer', color: 'blue', icon: PiBagSimple },
  email: { label: 'Email', color: 'pink', icon: PiEnvelopeSimple },
  engagement: { label: 'Engagement', color: 'cyan', icon: PiCursorClick },
  kyc: { label: 'KYC', color: 'grape', icon: PiShieldCheck },
  payment: { label: 'Payment', color: 'teal', icon: PiReceipt },
  payer: { label: 'Paying party', color: 'orange', icon: PiUserSwitch },
  split: { label: 'Split workflow', color: 'indigo', icon: PiGitBranch },
};

const CATEGORY_ORDER: Category[] = [
  'note',
  'offer',
  'email',
  'engagement',
  'kyc',
  'payment',
  'payer',
  'split',
];

type FeedItem = {
  key: string;
  time: string;
  text: string;
  category: Category;
  by?: string;
  tone?: 'error' | 'warning';
  author?: string;
  commentId?: number;
  canDelete?: boolean;
  payerLink?: { id: number; name: string };
  suffix?: string;
};

function emailTone(status?: string): 'error' | 'warning' | undefined {
  if (!status) return undefined;
  if (['bounced', 'failed', 'complained'].includes(status)) return 'error';
  if (['submission_unknown', 'queued', 'unsubscribed'].includes(status)) {
    return 'warning';
  }
  return undefined;
}

function categorizeUser(action: number): Category {
  if (action === 26 || action === 27) return 'payer';
  if (action === 28 || action === 29) return 'payment';
  if (action === 40 || action === 41) return 'split';
  return 'offer';
}

function categorizeCustomer(action: number): Category {
  if (action >= 20 && action <= 25) return 'kyc';
  if (action === 13 || action === 30 || action === 31) return 'payment';
  if (action === 40 || action === 51 || action === 52) return 'payer';
  return 'engagement';
}

const toneColor = (tone?: 'error' | 'warning') =>
  tone === 'error' ? 'red' : tone === 'warning' ? 'orange' : undefined;

export default function OfferActivityFeed({
  offerId,
  activity,
  splitAudit,
  currentUserId,
  canMutate,
}: {
  offerId: number;
  activity: TOfferActivity[];
  splitAudit?: SplitAuditEvent[];
  currentUserId?: number;
  canMutate: boolean;
}) {
  const { data: comments = [] } = useOfferComments(offerId);
  const addComment = useAddOfferComment();
  const delComment = useDeleteOfferComment();
  const [body, setBody] = useState('');
  const [hidden, setHidden] = useState<Set<Category>>(new Set());

  const items = useMemo(() => {
    const offerItems: FeedItem[] = activity.map((a, i) => {
      const item: FeedItem = {
        key: `offer-${i}-${a.time}`,
        time: a.time,
        text: formatActivity(a),
        category:
          a.source === 'email'
            ? 'email'
            : a.source === 'user'
              ? categorizeUser(a.action)
              : categorizeCustomer(a.action),
        tone:
          a.source === 'email'
            ? emailTone(a.meta.status as string | undefined)
            : a.source === 'customer' && a.action === 14
              ? 'error'
              : a.source === 'customer' && a.action === 24
                ? 'warning'
                : undefined,
      };
      if (a.source === 'user' && (a.action === 26 || a.action === 27)) {
        const meta = a.meta as Record<string, unknown> | undefined;
        const payerId = meta?.payerId as number | undefined;
        if (payerId) {
          const name =
            (meta?.payerDisplayName as string | undefined) || `#${payerId}`;
          const by = meta?.by as string | undefined;
          item.text = 'Payer';
          item.payerLink = { id: payerId, name };
          item.suffix = `${a.action === 26 ? 'attached' : 'detached'}${by ? ` by ${by}` : ''}`;
        }
      }
      return item;
    });

    const splitItems: FeedItem[] = (splitAudit ?? []).map((e) => ({
      key: `split-${e.id}`,
      time: e.time,
      text: e.detail ? `${e.action} — ${e.detail}` : e.action,
      category: 'split',
      by: e.actor,
    }));

    const commentItems: FeedItem[] = comments.map((c) => ({
      key: `comment-${c.id}`,
      time: c.createdAt,
      text: c.body,
      category: 'note',
      author: c.authorEmail ?? 'Note',
      commentId: c.id,
      canDelete: canMutate || currentUserId === c.authorUserId,
    }));

    return [...offerItems, ...splitItems, ...commentItems].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );
  }, [activity, splitAudit, comments, canMutate, currentUserId]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) c[it.category] = (c[it.category] || 0) + 1;
    return c;
  }, [items]);

  const presentCats = CATEGORY_ORDER.filter((c) => counts[c]);
  const shown = items.filter((it) => !hidden.has(it.category));

  const toggle = (cat: Category) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  // group shown items by month (already sorted desc)
  const months: { label: string; items: FeedItem[] }[] = [];
  for (const item of shown) {
    const label = dayjs(item.time).format('MMMM YYYY');
    const prev = months[months.length - 1];
    if (prev && prev.label === label) prev.items.push(item);
    else months.push({ label, items: [item] });
  }

  return (
    <Stack gap="md">
      {/* Note composer */}
      {canMutate && (
        <Paper withBorder p="sm" radius="md">
          <Textarea
            placeholder="Add a note..."
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            autosize
            minRows={2}
            variant="unstyled"
          />
          <Group justify="flex-end" mt="xs">
            <Button
              size="xs"
              disabled={!body.trim() || addComment.isPending}
              onClick={async () => {
                await addComment.mutateAsync({ offerId, body });
                setBody('');
              }}
            >
              Add note
            </Button>
          </Group>
        </Paper>
      )}

      <Group justify="space-between">
        <Text fw={500} fz="sm">
          Activity
        </Text>
        <Menu
          closeOnItemClick={false}
          position="bottom-end"
          width={220}
          withinPortal
        >
          <Menu.Target>
            <Button
              variant="default"
              size="xs"
              rightSection={<PiCaretDown size={12} />}
            >
              Filter activity ({shown.length}/{items.length})
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Stack gap={8} p="xs">
              {presentCats.length === 0 && (
                <Text fz="xs" c="dimmed">
                  Nothing to filter yet.
                </Text>
              )}
              {presentCats.map((cat) => {
                const meta = CATEGORIES[cat];
                return (
                  <Checkbox
                    key={cat}
                    size="xs"
                    checked={!hidden.has(cat)}
                    onChange={() => toggle(cat)}
                    label={
                      <Group gap={6} wrap="nowrap">
                        <ThemeIcon
                          size={16}
                          radius="xl"
                          variant="light"
                          color={meta.color}
                        >
                          <meta.icon size={10} />
                        </ThemeIcon>
                        <Text fz="xs">
                          {meta.label} ({counts[cat]})
                        </Text>
                      </Group>
                    }
                  />
                );
              })}
              <Divider my={2} />
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => setHidden(new Set())}
                >
                  Select all
                </Button>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="gray"
                  onClick={() => setHidden(new Set(presentCats))}
                >
                  Clear
                </Button>
              </Group>
            </Stack>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {items.length === 0 ? (
        <Text fz="xs" c="dimmed">
          No activity yet.
        </Text>
      ) : months.length === 0 ? (
        <Text fz="xs" c="dimmed">
          No activity matches the current filter.
        </Text>
      ) : (
        <Stack gap="md">
          {months.map((month) => (
            <Stack key={month.label} gap={4}>
              <Text fw={600} fz="xs" c="dimmed" tt="uppercase">
                {month.label}
              </Text>
              <Stack gap={0}>
                {month.items.map((item) => {
                  const meta = CATEGORIES[item.category];
                  const bulletColor = toneColor(item.tone) || meta.color;
                  const isNote = item.category === 'note';
                  return (
                    <Group
                      key={item.key}
                      gap={8}
                      wrap="nowrap"
                      align={isNote ? 'flex-start' : 'center'}
                      className={classes.row}
                    >
                      <Box
                        className={classes.icon}
                        c={`${bulletColor}.6`}
                        component={meta.icon}
                        size={16}
                      />
                      {isNote ? (
                        <Box className={`${classes.body} ${classes.note}`}>
                          <Paper
                            withBorder
                            px="xs"
                            py={4}
                            radius="sm"
                            bg="var(--mantine-color-yellow-light)"
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Group gap={6} wrap="nowrap" align="flex-start">
                              <Text
                                fz="sm"
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {item.text}
                              </Text>
                              <Text
                                fz="xs"
                                c="dimmed"
                                style={{ flexShrink: 0 }}
                              >
                                {item.author} ·{' '}
                                {dayjs(item.time).format('DD MMM, HH:mm')}
                              </Text>
                              {item.canDelete && (
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="xs"
                                  onClick={() =>
                                    delComment.mutate({
                                      id: item.commentId!,
                                      offerId,
                                    })
                                  }
                                >
                                  <PiTrash size={12} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Paper>
                        </Box>
                      ) : (
                        <Box className={classes.body}>
                          {item.by && (
                            <Badge size="xs" variant="light" color={meta.color}>
                              {item.by}
                            </Badge>
                          )}
                          <Text
                            fz="sm"
                            c={toneColor(item.tone)}
                            style={{ flex: 1, minWidth: 0 }}
                            lineClamp={1}
                          >
                            {item.text}
                            {item.payerLink && (
                              <>
                                {' '}
                                <Anchor
                                  component={Link}
                                  to={`~/payers/${item.payerLink.id}`}
                                  fz="sm"
                                  fw={500}
                                >
                                  {item.payerLink.name}
                                </Anchor>
                              </>
                            )}
                            {item.suffix && ` ${item.suffix}`}
                          </Text>
                          <Text fz="xs" c="dimmed" style={{ flexShrink: 0 }}>
                            {dayjs(item.time).format('DD MMM, HH:mm')}
                          </Text>
                        </Box>
                      )}
                    </Group>
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
